import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminEventDetail,
  EventInput,
  EventTranslationInput,
  ListResponse,
  PublicEventListItem,
} from '../types';
import { api } from './client';
import { qk } from './keys';

export interface EventsListParams {
  locale: string;
  page?: number;
  pageSize?: number;
  status?: 'draft' | 'published' | 'cancelled';
  from?: string;
  to?: string;
}

export function useEvents(params: EventsListParams, opts?: { admin?: boolean }) {
  const path = opts?.admin ? '/admin/events' : '/events';
  return useQuery({
    queryKey: qk.events({ ...params, admin: opts?.admin === true }),
    queryFn: () => api.get<ListResponse<PublicEventListItem>>(path, params),
  });
}

/** Admin event detail — includes drafts/cancelled and the per-locale translations. */
export function useEvent(slug: string | null, locale: string) {
  return useQuery({
    queryKey: qk.event(slug ?? '', `${locale}|admin`),
    enabled: !!slug,
    queryFn: () =>
      api.get<AdminEventDetail>(`/admin/events/${encodeURIComponent(slug ?? '')}`, { locale }),
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EventInput) =>
      api.post<{ id: string; slug: string }>('/admin/events', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; input: Partial<EventInput> }) =>
      api.patch(`/admin/events/${args.id}`, args.input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/events/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

export function useUpsertEventTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; locale: string; input: EventTranslationInput }) =>
      api.put(`/admin/events/${args.id}/translations/${args.locale}`, args.input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

export function useDeleteEventTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; locale: string }) =>
      api.delete(`/admin/events/${args.id}/translations/${args.locale}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

export function useBulkDeleteEvents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ deleted: number }>('/admin/events/bulk-delete', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}
